/**
 * ADR-0214 C7 — the Activity trail (owner: "breadcrumbs break on prod"). One
 * trail, in the order walked: Activity › <card> › <person> › <subject>. Each
 * step keeps its address WITH its query (period, audience, work), so a crumb
 * puts the page back as it was. The trail rides in the router's location state;
 * every link inside the drill hands it on, and a study or knowledge entry
 * opened from it shows it above its own breadcrumb.
 */

export interface TrailStep {
  to: string;
  label: string;
}

export interface TrailState {
  activityTrail: TrailStep[];
}

/** Only drill addresses ride the trail; anything else in state is ignored. */
const DRILL = /^\/app\/(activity|subjects)(\/|\?|$)/;
const MAX_STEPS = 12;

const pathOf = (to: string) => to.split("?")[0]!;

/** The trail in a location's state, or none. */
export function readTrail(state: unknown): TrailStep[] {
  const t = (state as { activityTrail?: unknown } | null)?.activityTrail;
  if (!Array.isArray(t)) return [];
  return t
    .filter((s): s is TrailStep => !!s && typeof (s as TrailStep).to === "string" && typeof (s as TrailStep).label === "string" && DRILL.test((s as TrailStep).to))
    .slice(-MAX_STEPS);
}

/** The steps above this page: the trail walked, cut where this page already sits on it (walking back never loops). */
export function trailAbove(trail: TrailStep[], herePath: string): TrailStep[] {
  const i = trail.findIndex((s) => pathOf(s.to) === herePath);
  return i >= 0 ? trail.slice(0, i) : trail;
}

/** The state a link on this page hands on: the steps above it, then this page as it is now. */
export function trailOnward(trail: TrailStep[], here: TrailStep): TrailState {
  return { activityTrail: [...trailAbove(trail, pathOf(here.to)), here].slice(-MAX_STEPS) };
}

/** The period the trail was walked in (its latest step with one), for a page whose address has none. */
export function trailPeriod(trail: TrailStep[]): { from: string; to: string } | null {
  for (let i = trail.length - 1; i >= 0; i--) {
    const q = new URLSearchParams(trail[i]!.to.split("?")[1] ?? "");
    const from = q.get("from");
    const to = q.get("to");
    if (from && to) return { from, to };
  }
  return null;
}
